import { Injectable } from '@angular/core';
import { ColDef } from '@ag-grid-community/core';
import {
  DocumentsData,
  DocumentType,
  FILTER_DEFAULT_TYPES,
  GridHeader,
  GridRowData,
  ProductSubscriptionsDTO,
  ProductsUserData,
  RowDataChange,
  SubscribedProductDTO,
  SubscriptionProductDTO,
  SubscriptionProductFilterOptions,
  SubscriptionProductFilterType,
  SubscriptionProductsData,
  SubscriptionProductSelectedFilters,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import snakeCase from 'lodash/snakeCase';
import { Logger } from '@utils/logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DropdownItem } from '@frk/eds-components';

/**
 * Logger
 */
const logger = Logger.getLogger('SubscriptionsManageComponent');

@Injectable({
  providedIn: 'root',
})
export class SubscriptionProductsService {
  private docTypes: GridHeader[];
  private rowData: GridRowData[];
  private userData: ProductsUserData;
  private subscriptionsOnly = false;
  private rowDataChange$: Subject<RowDataChange> = new Subject<RowDataChange>();
  private singleRowData$: Subject<GridRowData> = new Subject<GridRowData>();
  private isProductsModalVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private isPreferencesModalVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private isEmailPreferences$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  private expandedRowData$: Subject<DocumentsData[]> = new Subject<
    DocumentsData[]
  >();

  private selectedFilters$: Subject<
    SubscriptionProductSelectedFilters[]
  > = new Subject<SubscriptionProductSelectedFilters[]>();

  constructor() {
    // Constructor placeholder.
  }

  /**
   * Return default col definition for products table
   */
  public getDefaultColDefs(): ColDef {
    return {
      sortable: true,
      wrapText: true,
      autoHeight: false,
      resizable: false,
    };
  }

  /**
   * Convert id to snake upper cases
   * @param id - string
   */
  public toSnakeCase(id: string): string {
    return snakeCase(id).toUpperCase();
  }

  /**
   * Returns number of subscribed items.
   */
  public subscribedItemsCount(rowData: GridRowData[]): number {
    let subscribedItemsCount = 0;
    rowData.forEach((row: GridRowData) => {
      this.docTypes.forEach((docTypeHeader: GridHeader) => {
        logger.debug(row[docTypeHeader.id]);
        if (row[docTypeHeader.id]) {
          subscribedItemsCount++;
        }
      });
    });
    return subscribedItemsCount;
  }

  /**
   * Returns available document types
   */
  public getDocumentTypes = (): GridHeader[] => {
    return this.docTypes;
  };

  /**
   * Returns GridHeader object with document types
   * @param documentTypes - DocumentType array
   */
  public setDocumentTypes(documentTypes: DocumentType[]): void {
    this.docTypes = documentTypes.map((document: DocumentType) => {
      return {
        id: this.toSnakeCase(document.documentId),
        name: document.documentLabel,
        type: document.documentTypeId,
      };
    });
  }

  /**
   * Get ProductsUserData
   */
  public getUserData = (): ProductsUserData => {
    return this.userData;
  };

  /**
   * Set ProductsUserData
   * @param userData - ProductsUserData
   */
  public setUserData(userData: ProductsUserData): void {
    this.userData = userData;
  }

  /**
   * Get Subscriptions Only
   */
  public getSubscriptionsOnly = (): boolean => {
    return this.subscriptionsOnly;
  };

  /**
   * Set Subscriptions Only
   * @param subscriptionsOnly - boolean
   */
  public setSubscriptionsOnly(subscriptionsOnly: boolean): void {
    this.subscriptionsOnly = subscriptionsOnly;
  }

  /**
   * Get Subscribe Only Filter
   * @param data - GridRowData
   */
  public getSubscriptionOnlyFilter(data: GridRowData): boolean {
    if (this.subscriptionsOnly) {
      return this.docTypes.some((field: GridHeader) => {
        return data[field.id] === true;
      });
    }
    return true;
  }

  /**
   * Get Row Data
   */
  public getRowData(): GridRowData[] {
    return this.rowData;
  }

  /**
   * Update table data with changed data.
   * @param singleRowData - GridRowData.
   */
  public updateRowData(singleRowData: GridRowData): void {
    const rowToUpdate: GridRowData = this.rowData.find((row: GridRowData) => {
      return row.ID === singleRowData.ID;
    });
    const rowToUpdateIndex: number = this.rowData.indexOf(rowToUpdate);
    this.rowData[rowToUpdateIndex] = singleRowData;
  }

  /**
   * Set Row Data
   * @param productData - SubscriptionProductsData
   */
  public setRowData(productData: SubscriptionProductsData): void {
    this.rowData = productData?.products.map((data: SubscriptionProductDTO) => {
      const mappedData: GridRowData = {
        ID: data.productOneTis,
        PRODUCT_ID: data.productId,
        NAME: data.productLabel,
        ASSET_CLASS: data.productAssetClass,
      };
      // Mapping subscribed documents
      const subscribedProductData: ProductSubscriptionsDTO[] = this.userData.subscriptions.filter(
        (subscribedData: ProductSubscriptionsDTO) => {
          return subscribedData.productOneTis === data.productOneTis;
        }
      );
      mappedData.HAS_SUBSCRIPTION = subscribedProductData.length > 0;
      this.docTypes.forEach((header: GridHeader) => {
        mappedData[header.id] = subscribedProductData.some(
          (subscribedData: ProductSubscriptionsDTO) => {
            return (
              this.toSnakeCase(subscribedData.documentType) === header.id &&
              subscribedData.active
            );
          }
        );
      });
      return mappedData;
    });
    // Presorting data by name
    this.rowData.sort((a: GridRowData, b: GridRowData) => {
      if (a.NAME < b.NAME) {
        return -1;
      }
      return a.NAME > b.NAME ? 1 : 0;
    });
    logger.debug(this.rowData);
  }

  /**
   * Get Row Data Change Observable
   */
  public getRowDataChange$(): Observable<RowDataChange> {
    return this.rowDataChange$.asObservable();
  }

  /**
   * Set Row Data Change Observable
   * @param index - number
   * @param changedDocuments - GridRowData
   */
  public setRowDataChange$(changedDocuments: string[]): void {
    this.rowDataChange$.next({ changedDocuments });
  }

  /**
   * Get Submitted Products
   */
  public getSubmittedProducts = (): GridRowData[] => {
    return this.rowData.filter((rowData: GridRowData) => {
      let hasSelection = false;
      this.docTypes.forEach((docType: GridHeader) => {
        if (rowData[docType.id] === true || rowData.HAS_SUBSCRIPTION) {
          hasSelection = true;
        }
      });
      if (hasSelection) {
        return rowData;
      }
    });
  };

  /**
   * Set Submitted Docs
   * @param rowData - GridRowData
   *  @param selectedDocuments - DocumentsData
   */
  public getSubmittedDocumentsData(
    rowData: GridRowData,
    selectedDocuments: DocumentsData[]
  ): SubscribedProductDTO[] {
    if (selectedDocuments?.length) {
      const submittedDocs: SubscribedProductDTO[] = [];
      selectedDocuments.forEach((selectedDoc: DocumentsData) => {
        const submittedProduct: SubscribedProductDTO = {
          active: selectedDoc.isChecked,
          autoSubscribed: false,
          widenAssetId: selectedDoc?.widenAssetId,
          productId: rowData.PRODUCT_ID,
          documentTypeId: selectedDoc.documentId,
        };
        const subscriptionId: string = this.userData.subscriptions.find(
          (productSubscribed: ProductSubscriptionsDTO) => {
            return (
              (productSubscribed.productId === rowData.PRODUCT_ID &&
                productSubscribed.documentTypeId === selectedDoc.documentId) ||
              productSubscribed.widenAssetId === selectedDoc.widenAssetId
            );
          }
        )?.subscriptionId;
        if (subscriptionId) {
          submittedProduct.subscriptionId = subscriptionId;
        }
        if (subscriptionId || (selectedDoc.documentId && rowData.PRODUCT_ID)) {
          submittedDocs.push(submittedProduct);
        }
      });
      logger.debug('submittedDocs', submittedDocs);
      return submittedDocs;
    }
  }

  /**
   * Set Submitted Docs
   * @param submittedProducts - GridRowData
   */
  public getSubmittedDocs(
    submittedProducts: GridRowData[]
  ): SubscribedProductDTO[] {
    const submittedDocs: SubscribedProductDTO[] = [];
    submittedProducts.forEach((product: GridRowData) => {
      this.docTypes.forEach((docType: GridHeader) => {
        const submittedProduct: SubscribedProductDTO = {
          productId: product.PRODUCT_ID,
          documentTypeId: docType.type,
          active: product[docType.id],
          autoSubscribed: false,
        };
        const subscriptionId: string = this.userData.subscriptions.find(
          (productSubscribed: ProductSubscriptionsDTO) => {
            return (
              productSubscribed.productId === product.PRODUCT_ID &&
              productSubscribed.documentTypeId === docType.type
            );
          }
        )?.subscriptionId;
        if (subscriptionId) {
          submittedProduct.subscriptionId = subscriptionId;
        }
        if (subscriptionId || product[docType.id]) {
          submittedDocs.push(submittedProduct);
        }
      });
    });
    logger.debug('submittedDocs', submittedDocs);
    return submittedDocs;
  }

  /**
   * Returns true if all checkboxes are selected.
   * @param dataParams - Grid Row Data parameters
   * @param docTypesHeaders - Grid Header names
   */
  public checkSelectedAll(
    dataParams: GridRowData,
    docTypesHeaders: GridHeader[]
  ): boolean {
    const selection = [];
    docTypesHeaders.forEach((docTypeHeader: GridHeader) => {
      selection.push(dataParams[docTypeHeader.id]);
    });
    return selection.every((value) => value === true);
  }

  /**
   * Get Products Modal visibility
   */
  public getIsProductsModalVisible$(): Observable<boolean> {
    return this.isProductsModalVisible$.asObservable();
  }

  /**
   * Set Products Modal visibility
   * @param isVisible - boolean
   */
  public setIsProductsModalVisible$(isVisible: boolean) {
    this.isProductsModalVisible$.next(isVisible);
  }

  /**
   * Get Preferences Modal visibility
   */
  public getIsPreferencesModalVisible$(): Observable<boolean> {
    return this.isPreferencesModalVisible$.asObservable();
  }

  /**
   * Set Preferences Modal visibility
   * @param isVisible - boolean
   */
  public setIsPreferencesModalVisible$(isVisible: boolean) {
    this.isPreferencesModalVisible$.next(isVisible);
  }

  /**
   * Get Row Document Data
   */
  public getExpandedRowDocumentsData$(): Observable<DocumentsData[]> {
    return this.expandedRowData$.asObservable();
  }

  /**
   * Set Row Document Data
   * @param selectedDocuments - DocumentsData
   */
  public setExpandedRowDocumentsData(
    selectedDocuments: Array<DocumentsData>
  ): void {
    this.expandedRowData$.next(selectedDocuments);
  }

  /**
   * Get Single Row Data Observable
   */
  public getSingleRowData$(): Observable<GridRowData> {
    return this.singleRowData$.asObservable();
  }

  /**
   * Set Single Row Data Behaviour Subject
   * @param singleRowData - GridRowData
   */
  public setSingleRowData$(singleRowData: GridRowData) {
    this.singleRowData$.next(singleRowData);
  }

  /**
   * Get Is Email Preferences as Observable
   */
  public getIsEmailPreferences$(): Observable<boolean> {
    return this.isEmailPreferences$.asObservable();
  }

  /**
   * Set Is Email Preferences Behaviour Subject
   * @param isEmailPreferences - boolean
   */
  public setIsEmailPreferences$(isEmailPreferences: boolean) {
    this.isEmailPreferences$.next(isEmailPreferences);
  }

  /**
   * Get selected filters
   */
  public getSelectedFilters$(): Observable<
    SubscriptionProductSelectedFilters[]
  > {
    return this.selectedFilters$.asObservable();
  }

  /**
   * Set selected filters
   * @param isVisible - boolean
   */
  public setSelectedFilters$(
    selectedFilters: SubscriptionProductSelectedFilters[]
  ) {
    this.selectedFilters$.next(selectedFilters);
  }

  /**
   * set the filter data from the api
   */
  public extractFilterData(
    filterData: SubscriptionProductFilterOptions[]
  ): SubscriptionProductFilterType[] {
    const subscriptionProductFilterType: SubscriptionProductFilterType[] = [];
    filterData?.forEach((data) => {
      const subscriptionProductFilterTypeItems: DropdownItem[] = [];
      data?.values?.forEach((item: string) => {
        if (item) {
          subscriptionProductFilterTypeItems.push({
            label: item,
            value: item,
            checked: false,
          });
        }
      });
      subscriptionProductFilterType.push({
        filterLabel: FILTER_DEFAULT_TYPES[data.groupLabel],
        filterName: FILTER_DEFAULT_TYPES[data.groupLabel],
        filterItems: subscriptionProductFilterTypeItems,
      });
    });
    return subscriptionProductFilterType;
  }
}
